import React, { Fragment, useEffect, useState } from 'react';
import { filter, get, getOr, has, isEmpty, last } from 'lodash/fp';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonToolbar, Icon, Panel, SelectPicker } from 'rsuite';
import {
  createGuestNote,
  updateGuestNote,
  fetchUserRoleOptions
} from '../../../redux/sagas/routines';
import {
  getAll as getUserRoleOptions,
  getFetching as getFetchigUserRoleOptions
} from '../../../redux/Guest/selectors/user-role-options';
import { getSaved, getSaving } from '../../../redux/Guest/selectors/note';
import * as PERMISSIONS from '../../../constants/permissions';
import { modifyAccess } from '../../../helpers/permission';
import { UserRoles } from '../../../helpers/config';
import AccessControl from '../../AccessControl';
import 'react-quill/dist/quill.snow.css';

const ActionPanel = styled(Panel)`
  margin-bottom: 20px;
  .rs-panel-body {
    padding: 10px;
  }
`;

const EditorWrapper = styled.div`
  margin-top: 10px;
  > .quill > .ql-container {
    height: 150px;
  }
`;

const EditorModule = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' }
    ],
    [{ color: [] }, { background: [] }],
    ['clean']
  ]
};

const EditorFormat = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'color',
  'background'
];

const NoteForm = ({ guestId, data, showForm, onHideForm, onShowForm }) => {
  const dispatch = useDispatch();
  const isSaved = useSelector(getSaved);
  const isSaving = useSelector(getSaving);
  const userRoleOptions = useSelector(getUserRoleOptions);
  const isFetchingUserRoleOptions = useSelector(getFetchigUserRoleOptions);
  const rolesOptions = filter(
    ({ value }) => has(value, UserRoles),
    userRoleOptions
  );
  const defaultRoleId = get('value', last(userRoleOptions));
  const [roleId, setRoleId] = useState(defaultRoleId);
  const [note, setNote] = useState('');
  const allowSave = note && roleId;

  useEffect(() => {
    dispatch(fetchUserRoleOptions());
  }, []);

  useEffect(
    () => {
      if (!isEmpty(data)) {
        setNote(data.note);
        setRoleId(data.roleId);
      }
    },
    [data]
  );

  useEffect(
    () => {
      if (isSaved) {
        onHideForm();
      }
    },
    [isSaved]
  );

  const handleNoteChange = value => {
    setNote(value);
  };

  const handleRoleChange = value => {
    setRoleId(value);
  };

  const handleOnCreate = () => {
    setNote('');
    setRoleId(defaultRoleId);
    onShowForm();
  };

  const handleSave = () => {
    const id = getOr(null, 'id', data);
    if (id) {
      dispatch(updateGuestNote({ id, body: { note, roleId } }));
    } else {
      dispatch(createGuestNote({ note, roleId, objId: guestId }));
    }
  };

  return (
    <ActionPanel bordered>
      <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MANAGE_NOTE)}>
        <ButtonToolbar style={{ textAlign: 'right' }}>
          {showForm ? (
            <Fragment>
              <Button
                onClick={handleSave}
                appearance="primary"
                disabled={!allowSave || isSaving}
              >
                <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button onClick={onHideForm}>
                <Icon icon="close-circle" /> Cancel
              </Button>
            </Fragment>
          ) : (
            <Button onClick={handleOnCreate} appearance="primary">
              <Icon icon="plus-circle" /> Create
            </Button>
          )}
        </ButtonToolbar>
      </AccessControl>
      {showForm && (
        <EditorWrapper>
          <ReactQuill
            theme="snow"
            value={note}
            modules={EditorModule}
            formats={EditorFormat}
            onChange={handleNoteChange}
            placeholder="Compose note (Required)"
          />
          <SelectPicker
            style={{ width: 300, marginRight: 10, marginTop: 10 }}
            placeholder="Select role (Required)"
            value={roleId}
            data={rolesOptions}
            appearance="default"
            onChange={handleRoleChange}
            disabled={isFetchingUserRoleOptions}
          />
        </EditorWrapper>
      )}
    </ActionPanel>
  );
};

NoteForm.propTypes = {
  guestId: PropTypes.number.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number,
    note: PropTypes.string,
    roleId: PropTypes.number
  }),
  showForm: PropTypes.bool,
  onHideForm: PropTypes.func.isRequired,
  onShowForm: PropTypes.func.isRequired
};

NoteForm.defaultProps = {
  showForm: false,
  data: { id: null, note: '', roleId: null }
};

export default NoteForm;
